import { initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  GoogleAuthProvider,
  initializeAuth,
  OAuthProvider,
  setPersistence,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { CustomProvider, getToken as getAppCheckToken, initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from 'firebase/app-check';
import { Capacitor } from '@capacitor/core';
import { FirebaseAppCheck as NativeFirebaseAppCheck } from '@capacitor-firebase/app-check';
import firebaseConfig from '../firebase-applet-config.json';
import { authDomainForHost } from './domain/authRedirect';

const runtimeFirebaseConfig = {
  ...firebaseConfig,
  authDomain: authDomainForHost(window.location.hostname, firebaseConfig.authDomain),
};

export const firebaseApp = initializeApp(runtimeFirebaseConfig);

// Firebase's default browser Auth initialization probes IndexedDB before
// falling back to localStorage. That probe can remain pending in a production
// WKWebView and blocks both onAuthStateChanged and every sign-in attempt. Native
// Capacitor builds select their persistence synchronously at initialization.
export const auth = Capacitor.isNativePlatform()
  ? initializeAuth(firebaseApp, { persistence: browserLocalPersistence })
  : getAuth(firebaseApp);
export const authPersistenceReady = Capacitor.isNativePlatform()
  ? Promise.resolve()
  : setPersistence(auth, browserLocalPersistence);

export const db = initializeFirestore(firebaseApp, {
  ignoreUndefinedProperties: true,
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

export const storage = getStorage(firebaseApp);
export const functions = getFunctions(firebaseApp, 'europe-north1');

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ?? 8180));
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}

const appCheckSiteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY || firebaseConfig.appCheckSiteKey;
const usingEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';
const appCheckDebugToken = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;
let appCheck: AppCheck | null = null;
let nativeAppCheckReady: Promise<unknown> = Promise.resolve();
const usesNativeAppCheck = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
let nativeForceRefreshRequested = false;
let appCheckAttempt: Promise<void> | null = null;
let lastForcedRefreshAt = 0;
let appCheckRetryAfter = 0;
let lastAppCheckError: unknown;
const APP_CHECK_RETRY_COOLDOWN_MS = 30_000;
if (import.meta.env.DEV && !usingEmulators) {
  (globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken || true;
}
if (usesNativeAppCheck && import.meta.env.PROD && !usingEmulators) {
  // iOS selects its App Check provider and configures Firebase in AppDelegate,
  // before Capacitor creates the web view. Initializing the provider here would
  // be too late on a cold launch, so JS only enables refresh and bridges its
  // App Attest/DeviceCheck token.
  nativeAppCheckReady = NativeFirebaseAppCheck.setTokenAutoRefreshEnabled({ enabled: true });
  appCheck = initializeAppCheck(firebaseApp, {
    provider: new CustomProvider({
      getToken: async () => {
        await nativeAppCheckReady;
        // Both SDK layers cache valid tokens. Only bypass the native cache when
        // ensureAppCheckReady explicitly asks for a controlled recovery; forcing
        // every sign-in attempt can exhaust Play Integrity's retry allowance.
        const forceRefresh = nativeForceRefreshRequested;
        nativeForceRefreshRequested = false;
        const result = await NativeFirebaseAppCheck.getToken({ forceRefresh });
        return { token: result.token, expireTimeMillis: result.expireTimeMillis ?? Date.now() + 55 * 60_000 };
      },
    }),
    isTokenAutoRefreshEnabled: true,
  });
} else if (appCheckSiteKey && !usingEmulators) {
  // Android's native Play Integrity and reCAPTCHA mobile providers can reject
  // legitimate verified aftermarket operating systems before sign-in. The
  // Capacitor bundle is served from the production https://tragenda.app origin,
  // so Android uses the same domain-restricted, invisible reCAPTCHA Enterprise
  // provider as the production web app. Firebase enforcement remains enabled;
  // this changes the attestation provider rather than bypassing App Check.
  appCheck = initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

/**
 * Makes security-sensitive callable requests deterministic on a native cold
 * start. Firebase Functions automatically attaches this App Check token to the
 * next callable request; callers can safely retry when device attestation is
 * temporarily unavailable.
 */
export async function ensureAppCheckReady(forceRefresh = false) {
  if (usingEmulators || !appCheck) return;
  if (appCheckAttempt) return appCheckAttempt;

  const now = Date.now();
  if (now < appCheckRetryAfter && lastAppCheckError) throw lastAppCheckError;

  const shouldForceRefresh = forceRefresh && now - lastForcedRefreshAt >= APP_CHECK_RETRY_COOLDOWN_MS;
  if (shouldForceRefresh) {
    lastForcedRefreshAt = now;
    nativeForceRefreshRequested = usesNativeAppCheck;
  }

  appCheckAttempt = (async () => {
    await nativeAppCheckReady;
    await getAppCheckToken(appCheck!, shouldForceRefresh);
  })();

  try {
    await appCheckAttempt;
    appCheckRetryAfter = 0;
    lastAppCheckError = undefined;
  } catch (error) {
    nativeForceRefreshRequested = false;
    appCheckRetryAfter = Date.now() + APP_CHECK_RETRY_COOLDOWN_MS;
    lastAppCheckError = error;
    throw error;
  } finally {
    appCheckAttempt = null;
  }
}

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);
